import React from "react";
import { cn } from "@/lib/utils";

/**
 * Tiny SVG sparkline + dots for the last-N scores.
 * Values come in oldest→newest. Renders fixed [0,100] band.
 * Renders nothing if 0 values; renders a centered dot if 1 value.
 */
export const Sparkline = ({
    values = [],
    width = 280,
    height = 80,
    className,
    dataTestId,
}) => {
    const padX = 8;
    const padY = 12;
    const innerW = width - padX * 2;
    const innerH = height - padY * 2;

    if (!values || values.length === 0) {
        return (
            <div
                className={cn("flex h-20 w-full items-center justify-center text-xs text-muted-foreground", className)}
                data-testid={dataTestId}
            >
                Practice a few sessions to see your score trend here.
            </div>
        );
    }

    // Always render against a fixed 0–100 band so dips read honestly.
    const xFor = (i) => (values.length === 1 ? width / 2 : padX + (i / (values.length - 1)) * innerW);
    const yFor = (v) => padY + (1 - v / 100) * innerH;

    const points = values.map((v, i) => [xFor(i), yFor(v)]);
    const path = points
        .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`)
        .join(" ");
    const last = points[points.length - 1];

    return (
        <svg
            viewBox={`0 0 ${width} ${height}`}
            width="100%"
            height={height}
            className={cn("overflow-visible", className)}
            data-testid={dataTestId}
            aria-label={`Score trend across last ${values.length} sessions`}
        >
            {/* faint baselines for 50 + 70 */}
            <line
                x1={padX}
                x2={width - padX}
                y1={yFor(50)}
                y2={yFor(50)}
                stroke="hsl(var(--border))"
                strokeDasharray="3 4"
                strokeWidth="1"
            />
            <line
                x1={padX}
                x2={width - padX}
                y1={yFor(70)}
                y2={yFor(70)}
                stroke="hsl(var(--border))"
                strokeDasharray="3 4"
                strokeWidth="1"
            />
            {values.length > 1 && (
                <>
                    <path
                        d={`${path} L ${last[0]} ${height - padY} L ${points[0][0]} ${height - padY} Z`}
                        fill="hsl(var(--primary))"
                        fillOpacity="0.08"
                    />
                    <path
                        d={path}
                        fill="none"
                        stroke="hsl(var(--primary))"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </>
            )}
            {points.map(([x, y], i) => (
                <circle
                    key={i}
                    cx={x}
                    cy={y}
                    r={i === points.length - 1 ? 4 : 3}
                    fill={i === points.length - 1 ? "hsl(var(--primary))" : "white"}
                    stroke="hsl(var(--primary))"
                    strokeWidth="1.5"
                    data-testid={`sparkline-dot-${i}`}
                />
            ))}
            {/* last value label */}
            <text
                x={last[0]}
                y={Math.max(last[1] - 8, 10)}
                textAnchor="middle"
                className="font-semibold"
                fontSize="11"
                fill="hsl(var(--foreground))"
            >
                {values[values.length - 1]}
            </text>
        </svg>
    );
};
